/**
 * `ui` store —— Toast / 全局弹层 / 图片查看器。
 * （ARCH 中列在 T05，因 T02 的认证流程已需要 Toast 反馈，提前落地。）
 */

import { create } from 'zustand';
import type { ErrorAction, ErrorCode } from '@shared/errors';
import { errorCatalog } from '@shared/errors';

export type ToastKind = 'info' | 'success' | 'warn' | 'error';

export interface Toast {
  id: string;
  kind: ToastKind;
  text: string;
  durationMs: number;
  /**
   * 可执行动作标签（唯一来源 `errorCatalog[code].action`）。缺省 ⇒ 不渲染动作按钮，
   * 既有 18 处 `pushToast` 调用行为不变。
   */
  action?: ErrorAction;
  /** 动作处理器：`retry` 场景由发起方注入；`topup`/`unlock`/`switchRelay` 走内置路由。 */
  onAction?: () => void;
}

/** `pushError` 的可选行为（向后兼容：第 1 参不变，仅追加可选第 2 参）。 */
export interface PushErrorOptions {
  /** true ⇒ 走 `ErrorBanner` 驻留横幅（不再弹 Toast，避免重复）；用于链路级/阻塞性错误。缺省 false。 */
  global?: boolean;
  /** 可执行动作处理器：`retry` 必传；其余可覆盖内置路由。 */
  onAction?: () => void;
}

export interface UiState {
  toasts: Toast[];
  globalError?: ErrorCode;
  imageViewer?: { cid: string; thumbCid?: string };
  // —— 兼容：第 1~3 参不变，仅追加可选第 4 参 ——
  pushToast(kind: ToastKind, text: string, durationMs?: number, extra?: Pick<Toast, 'action' | 'onAction'>): string;
  // —— 兼容：第 1 参不变，仅追加可选第 2 参 ——
  pushError(code: ErrorCode, opts?: PushErrorOptions): string;
  dismissToast(id: string): void;
  setGlobalError(code?: ErrorCode): void;
  clearGlobalError(): void;
  openImageViewer(cid: string, thumbCid?: string): void;
  closeImageViewer(): void;
}

export const useUiStore = create<UiState>((set, get) => ({
  toasts: [],
  globalError: undefined,
  imageViewer: undefined,

  pushToast: (kind, text, durationMs = 3200, extra) => {
    const id = `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    set((state) => ({
      toasts: [...state.toasts, { id, kind, text, durationMs, action: extra?.action, onAction: extra?.onAction }],
    }));
    if (typeof window !== 'undefined') {
      window.setTimeout(() => get().dismissToast(id), durationMs);
    }
    return id;
  },

  /**
   * 统一入口：错误只显示 `errorCatalog` 的人话文案，绝不直显 err.message。
   * `opts.global` ⇒ 走驻留横幅（`globalError`），不再弹 Toast；否则带 `action`/`onAction` 弹 Toast。
   * 动作标签一律取自 `errorCatalog[code].action`（单一数据源，UI 禁硬编码映射）。
   */
  pushError: (code, opts) => {
    if (opts?.global) {
      set({ globalError: code });
      return '';
    }
    const entry = errorCatalog[code] ?? errorCatalog.E_UNKNOWN;
    return get().pushToast('error', entry.text, 4200, { action: entry.action, onAction: opts?.onAction });
  },

  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),

  setGlobalError: (code) => set({ globalError: code }),

  clearGlobalError: () => set({ globalError: undefined }),

  openImageViewer: (cid, thumbCid) => set({ imageViewer: { cid, thumbCid } }),
  closeImageViewer: () => set({ imageViewer: undefined }),
}));

/** 便捷：在组件外也能弹 Toast。 */
export const toast = {
  info: (text: string) => useUiStore.getState().pushToast('info', text),
  success: (text: string) => useUiStore.getState().pushToast('success', text),
  warn: (text: string) => useUiStore.getState().pushToast('warn', text),
  error: (code: ErrorCode) => useUiStore.getState().pushError(code),
};
